import React, { useMemo, useState, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import CollapsibleTable from '../../components/table';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';
import CancelacionModal from '../../components/CancelacionModal';
import SnackbarAlert from '../../components/SnackbarAlert';
import FileActionModal from '../../components/FileActionModal';
import ExportButton from '../../components/BotonEx';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DoDisturbOffIcon from '@mui/icons-material/DoDisturbOff';
import FolderIcon from '@mui/icons-material/Folder';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import axios from 'axios';
import { usePagosAutController } from './hooks/usePagosAutController';
import config from '../../config/config';

const buildCentroCostoOption = (row = {}) => {
  const numero = String(row.NUMEROCC || row.NUMERO_CENTRO_COSTO || row.NUMERO_CC || '').trim();
  const nombre = String(row.CENTROCOSTO || row.CENTRO_COSTO || row.NOMBRE_CC || '').trim();

  if (numero && nombre && numero.toUpperCase() !== nombre.toUpperCase()) return `${numero} - ${nombre}`;
  return numero || nombre;
};

const getUniqueOptions = (values = []) => (
  Array.from(new Set(values.map(value => String(value ?? '').trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, 'es'))
);

const getSelectedSummary = (values = []) => {
  if (!values.length) return '';
  if (values.length === 1) return values[0];
  return `${values.length} seleccionadas`;
};

const compactAutocompleteSx = {
  '& .MuiInputBase-root': {
    height: '28px !important',
    minHeight: '28px !important',
    maxHeight: '28px !important',
    borderRadius: '10px !important',
    bgcolor: '#ffffff',
    fontSize: '0.68rem',
    fontWeight: 500,
    px: '8px !important',
    py: '0px !important',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap !important',
    overflow: 'hidden',
    boxSizing: 'border-box',
    '& fieldset': {
      borderColor: '#cbd5e1 !important',
      top: 0,
      '& legend': { display: 'none' }
    },
    '&:hover fieldset': { borderColor: '#94a3b8 !important' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5 !important', borderWidth: '1.5px !important' },
  },
  '& .MuiAutocomplete-inputRoot': {
    paddingTop: '0px !important',
    paddingBottom: '0px !important',
  },
  '& .MuiAutocomplete-input': {
    minWidth: '0px !important',
    py: '0px !important',
    height: '100% !important',
    fontSize: '0.68rem !important',
    color: '#0f172a',
    boxSizing: 'border-box',
    '&::placeholder': {
      color: '#64748b',
      opacity: 1,
      fontWeight: 500,
    }
  },
  '& .MuiAutocomplete-endAdornment': {
    position: 'static !important',
    transform: 'none !important',
    display: 'flex',
    alignItems: 'center',
    ml: 'auto',
    '& .MuiIconButton-root': {
      p: '2px !important',
      fontSize: '14px !important',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '14px !important',
    }
  },
  '& .MuiInputLabel-root': {
    display: 'none',
  },
};

const renderSummaryInput = (params, label, selectedValues = []) => {
  const summary = getSelectedSummary(selectedValues);

  return (
    <TextField
      {...params}
      placeholder={summary ? undefined : label}
      InputProps={{
        ...params.InputProps,
        startAdornment: summary ? (
          <Box
            component="span"
            title={summary}
            sx={{
              maxWidth: 'calc(100% - 22px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '0.68rem',
              color: '#0f172a',
              fontWeight: 600,
              flexShrink: 1,
              pointerEvents: 'none',
              userSelect: 'none',
              display: 'inline-block',
              lineHeight: 1.2
            }}
          >
            {summary}
          </Box>
        ) : params.InputProps.startAdornment,
      }}
      sx={{
        '& .MuiAutocomplete-input': {
          width: summary ? '0px !important' : '100% !important',
          minWidth: summary ? '0px !important' : '20px !important',
          p: summary ? '0px !important' : undefined,
        }
      }}
    />
  );
};

const PagosAut = () => {
  const apiUrl = config.apiBaseUrl;

  // Catálogos jerárquicos
  const [proyectosCatalogo, setProyectosCatalogo] = useState([]);
  const [divisionesCatalogo, setDivisionesCatalogo] = useState([]);
  const [centrosCostoCatalogo, setCentrosCostoCatalogo] = useState([]);

  const {
    data,
    loadingData,
    loadingAction,
    tempFilterData,
    setTempFilterData,
    aplicarFiltros,
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    fileModalOpen,
    setFileModalOpen,
    fileList,
    initialFileId,
    modalStates,
    setModalStates,

    empresaIdPersist,
    empresaIds,
    baseFilters,
    nombreEmpresa,
    usuarioId,
    esTesorero,
    hiddenFieldsMemo,

    confirmAutorizarSolicitud,
    handleCancelarAutorizacion,
    cancelarSolicitud,
    handleViewFile,
    handleViewSpecificFile,
    procesarMasivo,
    handleExportFetchAll,
    handleTableStateChange,
  } = usePagosAutController({ centrosCostoCatalogo });

  const [selectedRows, setSelectedRows] = useState([]);
  const [quickSearch, setQuickSearch] = useState('');
  const [empresaOptions, setEmpresaOptions] = useState([]);
  const [filterOptionRows, setFilterOptionRows] = useState([]);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const latestTempFilterRef = React.useRef(tempFilterData);

  const initialColumnOrderMemo = useMemo(() => [
    'PAGOSAUT_OC_EMPRESA',
    'PAGOSAUT_CENTRO_DEPTO',
    'PAGOSAUT_COMPRADOR_FECHA',
    'TOTAL_DIVISA',
    'PAGOSAUT_IMPORTE',
    'PROVEEDOR',
    'PAGOSAUT_SEGUIMIENTO',
    'TESORERO1',
  ], []);

  const columnLabelsMemo = useMemo(() => ({
    PAGOSAUT_OC_EMPRESA: 'OC / Empresa',
    PAGOSAUT_CENTRO_DEPTO: 'CC / Departamento',
    PAGOSAUT_COMPRADOR_FECHA: 'Comprador / Fecha',
    TOTAL_DIVISA: 'Total / Divisa',
    PAGOSAUT_IMPORTE: 'Importe a pagar',
    PAGOSAUT_SEGUIMIENTO: 'Seguimiento',
    FOLIO: 'OC',
    FECHA: 'Fecha Solicitud',
    NOTAS: 'Notas Requi',
    NUMEROCC: 'CC',
    CENTROCOSTO: 'Nombre CC',
    DEPARTAMENTO: 'Departamento',
    FOLIO_FACTURA: 'Factura',
    NUMERO_ECO: 'Economico',
    PROVEEDOR: 'Proveedor',
    COMPRADOR: 'Comprador',
    CONCEPTOPAGO: 'Concepto Pago Usuario',
    CONCEPTO_PAGO_ERP: 'Tipo Pago ERP',
    TESORERO1: 'Tesorero',
    OBSERVACIONES: 'Observaciones Usuario',
    MONTOSOLICITADO: 'Importe a Pagar',
    TOTALOC: 'Total OC',
  }), []);

  React.useEffect(() => {
    latestTempFilterRef.current = tempFilterData;
  }, [tempFilterData]);

  // 1. Cargar Empresas asignadas al usuario
  React.useEffect(() => {
    const cargarEmpresas = async () => {
      if (!apiUrl || !usuarioId) return;

      try {
        const response = await axios.get(`${apiUrl}/api/empresas/${usuarioId}`);
        const empresas = response.data?.empresas || [];
        const mapped = empresas
          .map(empresa => ({
            id: Number(empresa.EMPRESAID),
            nombre: empresa.NOMBRE_EMPRESA,
          }))
          .filter(empresa => empresaIds.includes(empresa.id));
        setEmpresaOptions(mapped);

        if (mapped.length === 1) {
          const yaTieneEmpresa = (tempFilterData.EMPRESA_IDS || []).includes(mapped[0].id);
          if (!yaTieneEmpresa) {
            const updated = { ...tempFilterData, EMPRESA_IDS: [mapped[0].id], EMPRESA: mapped[0].nombre };
            setTempFilterData(updated);
          }
        }
      } catch (err) {
        console.error('Error cargando empresas para pagos aut:', err);
        const fallback = empresaIds.length === 1
          ? [{ id: empresaIds[0], nombre: nombreEmpresa || `Empresa ${empresaIds[0]}` }]
          : [];
        setEmpresaOptions(fallback);
      }
    };

    cargarEmpresas();
  }, [apiUrl, usuarioId, empresaIds, nombreEmpresa]);

  // 2. Cargar Opciones de filtros del catálogo según la(s) empresa(s)
  const selectedCatalogEmpresaIds = useMemo(() => {
    const selected = Array.isArray(tempFilterData.EMPRESA_IDS)
      ? tempFilterData.EMPRESA_IDS.map(Number).filter(id => Number.isInteger(id) && id > 0)
      : [];
    const scopedSelected = selected.filter(id => empresaIds.includes(id));
    if (scopedSelected.length) return scopedSelected;
    return empresaIds;
  }, [empresaIds, tempFilterData.EMPRESA_IDS]);

  // Refs para evitar llamadas duplicadas a la API si las empresas no cambiaron
  const lastLoadedOpcionesEmpresasRef = React.useRef(null);
  const lastLoadedCatalogEmpresasRef = React.useRef(null);

  React.useEffect(() => {
    const cargarOpciones = async () => {
      if (!apiUrl || !selectedCatalogEmpresaIds.length) {
        setFilterOptionRows([]);
        lastLoadedOpcionesEmpresasRef.current = null;
        return;
      }

      const empresasKey = selectedCatalogEmpresaIds.slice().sort().join(',');
      if (lastLoadedOpcionesEmpresasRef.current === empresasKey) return;
      lastLoadedOpcionesEmpresasRef.current = empresasKey;

      try {
        const response = await axios.post(`${apiUrl}/solicitudesAdmin/opcionesFiltros`, {
          idEmpresa: selectedCatalogEmpresaIds,
          filterData: {},
        });
        const rows = response.data?.rows || [];
        if (rows.length) {
          setFilterOptionRows(rows);
          return;
        }

        const departamentos = response.data?.departamentos || [];
        const centrosCosto = response.data?.centrosCosto || [];
        const proveedores = response.data?.proveedores || [];
        const divisas = response.data?.divisas || [];
        setFilterOptionRows([
          ...departamentos.map(DEPARTAMENTO => ({ DEPARTAMENTO })),
          ...centrosCosto.map(CENTROCOSTO => ({ CENTROCOSTO })),
          ...proveedores.map(PROVEEDOR => ({ PROVEEDOR })),
          ...divisas.map(DIVISA => ({ DIVISA })),
        ]);
      } catch (err) {
        console.error('Error cargando opciones de filtros para pagos aut:', err);
        setFilterOptionRows([]);
      }
    };

    cargarOpciones();
  }, [apiUrl, selectedCatalogEmpresaIds]);

  // 3. Cargar Proyectos, Divisiones y CCs jerárquicos
  React.useEffect(() => {
    if (!apiUrl || !selectedCatalogEmpresaIds.length) return;

    const empresasKey = selectedCatalogEmpresaIds.slice().sort().join(',');
    if (lastLoadedCatalogEmpresasRef.current === empresasKey) return;
    lastLoadedCatalogEmpresasRef.current = empresasKey;

    axios.post(`${apiUrl}/proyectosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setProyectosCatalogo(raw.filter(p => Boolean(p.ACTIVO)));
      })
      .catch(() => setProyectosCatalogo([]));

    axios.post(`${apiUrl}/divisionesTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setDivisionesCatalogo(raw.filter(d => Boolean(d.ACTIVO)));
      })
      .catch(() => setDivisionesCatalogo([]));

    axios.post(`${apiUrl}/centroCostosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setCentrosCostoCatalogo(raw.filter(c => Boolean(c.ACTIVO)));
      })
      .catch(() => setCentrosCostoCatalogo([]));
  }, [apiUrl, selectedCatalogEmpresaIds, usuarioId]);

  // Cascada de Proyectos
  const proyectoOptions = useMemo(() => {
    const allowedProyIds = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_PROYECTO)).filter(id => id > 0)
    );

    const map = new Map();
    proyectosCatalogo.forEach(p => {
      const pid = Number(p.ID_PROYECTO);
      if (centrosCostoCatalogo.length === 0 || allowedProyIds.has(pid)) {
        if (p.ID_PROYECTO && p.NOMBRE_PROYECTO) {
          map.set(pid, p.NOMBRE_PROYECTO);
        }
      }
    });
    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return list;
  }, [proyectosCatalogo, centrosCostoCatalogo]);

  // Cascada de Divisiones / Grupos
  const divisionOptions = useMemo(() => {
    const selectedProyIds = (tempFilterData.PROYECTOS || []).map(Number);
    const allowedDivIds = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_DIVISION)).filter(id => id > 0)
    );

    const map = new Map();
    divisionesCatalogo.forEach(d => {
      const pId = Number(d.ID_PROYECTO);
      const dId = Number(d.ID_DIVISION);
      const matchProy = !selectedProyIds.length || selectedProyIds.includes(pId);
      const matchAllowed = centrosCostoCatalogo.length === 0 || allowedDivIds.has(dId);

      if (matchProy && matchAllowed) {
        if (d.ID_DIVISION && d.NOMBRE_DIVISION) {
          map.set(dId, d.NOMBRE_DIVISION);
        }
      }
    });
    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return list;
  }, [divisionesCatalogo, tempFilterData.PROYECTOS, centrosCostoCatalogo]);

  const optionSourceRows = useMemo(() => (
    filterOptionRows.length ? filterOptionRows : (Array.isArray(data?.contenido) ? data.contenido : [])
  ), [data?.contenido, filterOptionRows]);

  const centroCostoOptions = useMemo(() => {
    const selectedProyIds = (tempFilterData.PROYECTOS || []).map(Number);
    const selectedDivIds = (tempFilterData.DIVISIONES || []).map(Number);

    if (centrosCostoCatalogo.length) {
      const filtered = centrosCostoCatalogo.filter(cc => {
        const pId = Number(cc.ID_PROYECTO);
        const dId = Number(cc.ID_DIVISION);
        if (selectedDivIds.length && !selectedDivIds.includes(dId)) return false;
        if (selectedProyIds.length && !selectedProyIds.includes(pId)) return false;
        return true;
      });

      const options = filtered.map(buildCentroCostoOption);
      return getUniqueOptions(options);
    }

    return getUniqueOptions(optionSourceRows.map(row => buildCentroCostoOption(row)));
  }, [centrosCostoCatalogo, tempFilterData.PROYECTOS, tempFilterData.DIVISIONES, optionSourceRows]);

  const departamentoOptions = useMemo(() => getUniqueOptions(optionSourceRows.map(row => row.DEPARTAMENTO)), [optionSourceRows]);
  const proveedorOptions = useMemo(() => getUniqueOptions(optionSourceRows.map(row => row.PROVEEDOR)), [optionSourceRows]);
  const divisaOptions = useMemo(() => getUniqueOptions(optionSourceRows.map(row => row.DIVISA)), [optionSourceRows]);

  const updateVisibleFilter = useCallback((field, value, options = {}) => {
    const { apply = false } = options;
    setTempFilterData(prev => {
      const updated = { ...prev, [field]: value };
      latestTempFilterRef.current = updated;
      if (apply) {
        aplicarFiltros(updated, quickSearch);
      }
      return updated;
    });
  }, [aplicarFiltros, quickSearch, setTempFilterData]);

  const applyFilters = useCallback((nextFilters = tempFilterData, nextQuickSearch = quickSearch) => {
    aplicarFiltros(nextFilters || latestTempFilterRef.current, nextQuickSearch);
  }, [aplicarFiltros, quickSearch, tempFilterData]);

  const clearFilters = useCallback(() => {
    setQuickSearch('');
    setTempFilterData(baseFilters);
    aplicarFiltros(baseFilters, '');
  }, [aplicarFiltros, baseFilters, setTempFilterData]);

  const buttons = useMemo(
    () => [
      {
        id: 1,
        label: 'Autorizar',
        onClick: confirmAutorizarSolicitud,
        icon: <TaskAltIcon />,
        buttonColor: '#43a047',
        buttonFontColor: '#ffffff',
        isVisible: true,
        isDisabled: false,
      },
      {
        id: 2,
        label: 'Rechazar',
        onClick: handleCancelarAutorizacion,
        icon: <DoDisturbOffIcon />,
        buttonColor: '#e53935',
        buttonFontColor: '#ffffff',
        isVisible: true,
        isDisabled: false,
      },
      {
        id: 6,
        label: 'Ver OC',
        onClick: (row) => handleViewSpecificFile(row, row.ID_OC),
        icon: <AssignmentTurnedInIcon />,
        buttonColor: '#ed6c02',
        buttonFontColor: '#ffffff',
        isVisible: true,
        isDisabled: (row) => !row.HAS_OC,
      },
      {
        id: 5,
        label: 'Ver Carátula',
        onClick: (row) => handleViewSpecificFile(row, row.ID_CB),
        icon: <AccountBalanceIcon />,
        buttonColor: '#0288d1',
        buttonFontColor: '#ffffff',
        isVisible: true,
        isDisabled: (row) => !row.HAS_CB,
      },
      {
        id: 4,
        label: 'Ver Factura',
        onClick: (row) => handleViewSpecificFile(row, row.ID_FACTURA),
        icon: <ReceiptIcon />,
        buttonColor: '#2e7d32',
        buttonFontColor: '#ffffff',
        isVisible: true,
        isDisabled: (row) => !row.HAS_FACTURA,
      },
      {
        id: 3,
        label: 'Visualizar Archivos',
        onClick: (row) => handleViewFile(row),
        icon: <FolderIcon />,
        buttonColor: '#607D8B',
        buttonFontColor: '#E3F2FD',
        isVisible: !esTesorero,
        isDisabled: false,
      },
    ],
    [
      confirmAutorizarSolicitud,
      handleCancelarAutorizacion,
      handleViewFile,
      handleViewSpecificFile,
      esTesorero,
    ]
  );

  const handleBulkAutorizar = useCallback(async () => {
    const success = await procesarMasivo(selectedRows, 'autorizar');
    if (success) setSelectedRows([]);
  }, [procesarMasivo, selectedRows]);

  const handleBulkRechazar = useCallback(async () => {
    const success = await procesarMasivo(selectedRows, 'cancelar');
    if (success) setSelectedRows([]);
  }, [procesarMasivo, selectedRows]);

  return (
    <Box sx={{ p: 'clamp(6px, 0.8vw, 12px)', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: '#f8fafc' }}>
      
      {/* ── BARRA SUPERIOR RESPONSIVA ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 'clamp(4px, 0.6vw, 8px)', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>

          {/* Vista Móvil / Laptop (< xl): [ 🔍 Filtros Avanzados • ∨ ] [ Buscador... ] */}
          <Box sx={{ display: { xs: 'flex', xl: 'none' }, alignItems: 'center', gap: 'clamp(4px, 0.6vw, 8px)', flex: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant={showFiltersPanel ? 'contained' : 'outlined'}
              onClick={(e) => {
                setFilterAnchorEl(e.currentTarget);
                setShowFiltersPanel(prev => !prev);
              }}
              startIcon={<SearchIcon sx={{ fontSize: 'clamp(12px, 0.9vw, 14px) !important' }} />}
              endIcon={showFiltersPanel ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
              sx={{
                height: 'clamp(26px, 2vw, 28px)',
                px: 'clamp(6px, 0.8vw, 10px)',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                fontWeight: 600,
                borderRadius: '10px',
                bgcolor: showFiltersPanel ? '#0f172a' : '#ffffff',
                borderColor: '#cbd5e1',
                color: showFiltersPanel ? '#ffffff' : '#334155',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: showFiltersPanel ? '#1e293b' : '#f8fafc', borderColor: '#94a3b8' },
              }}
            >
              Filtros Avanzados {((tempFilterData.DEPARTAMENTOS || []).length || (tempFilterData.CENTRO_COSTOS || []).length || (tempFilterData.PROVEEDORES || []).length || (tempFilterData.EMPRESA_IDS || []).length || (tempFilterData.PROYECTOS || []).length || (tempFilterData.DIVISIONES || []).length || (tempFilterData.DIVISAS || []).length) ? '•' : ''}
            </Button>

            <TextField
              disabled={loadingData}
              size="small"
              placeholder="Buscar..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyFilters(tempFilterData, e.currentTarget.value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#0655A5', fontSize: 'clamp(13px, 1vw, 15px)' }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={loadingData}
                      size="small"
                      onClick={() => {
                        setQuickSearch('');
                        applyFilters(tempFilterData, '');
                      }}
                      sx={{ p: 0.2 }}
                    >
                      <ClearIcon sx={{ fontSize: 'clamp(11px, 0.9vw, 13px)' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: { xs: 130, sm: 150, md: 160 },
                flex: 'none',
                '& .MuiOutlinedInput-root': {
                  height: 'clamp(26px, 2vw, 28px)',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: 'clamp(0.66rem, 0.62rem + 0.08vw, 0.72rem)',
                  fontWeight: 500,
                  px: 1,
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#0655A5' },
                }
              }}
            />
          </Box>

          {/* Vista Escritorio (>= xl): Barra Completa Estilizada con Cápsula de Fecha */}
          <Box
            sx={{
              display: { xs: 'none', xl: 'flex' },
              alignItems: 'center',
              gap: 'clamp(4px, 0.5vw, 7px)',
              bgcolor: '#ffffff',
              p: 'clamp(3px, 0.4vw, 5px)',
              px: 'clamp(6px, 0.8vw, 12px)',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 6px rgba(15, 23, 42, 0.03)',
              flex: 1,
              maxWidth: '100%',
              overflow: 'hidden'
            }}
          >
            {/* 1. Rango de Fechas Integrado Estilo Cápsula */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, bgcolor: '#ffffff', px: 0.8, py: 0.1, borderRadius: '10px', border: '1px solid #cbd5e1', height: 'clamp(26px, 2vw, 28px)', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 700, color: '#64748b' }}>Del</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_INICIO || ''}
                onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: false })}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{ width: 96, '& .MuiInputBase-input': { fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 600, color: '#0f172a', py: 0 } }}
              />
              <Typography sx={{ fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 700, color: '#64748b' }}>al</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_FIN || ''}
                onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: false })}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{ width: 96, '& .MuiInputBase-input': { fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 600, color: '#0f172a', py: 0 } }}
              />
            </Box>

            {/* 2. Select Empresa */}
            <Autocomplete
              disabled={loadingData}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={empresaOptions}
              value={empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id))}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  EMPRESA_IDS: value.map(empresa => empresa.id),
                  EMPRESA: value.map(empresa => empresa.nombre).join(','),
                  PROYECTOS: [],
                  PROYECTO: '',
                  DIVISIONES: [],
                  DIVISION: '',
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: '',
                  DEPARTAMENTOS: [],
                  PROVEEDORES: [],
                  DIVISAS: [],
                };
                setTempFilterData(updated);
              }}
              limitTags={1}
              renderTags={() => null}
              getOptionLabel={(option) => option?.nombre || ''}
              renderInput={(params) => (
                renderSummaryInput(
                  params,
                  'Empresa',
                  empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id)).map(e => e.nombre)
                )
              )}
              sx={{ width: 'clamp(100px, 8.5vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 3. Select Proyecto (Nivel 2) */}
            <Autocomplete
              disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={proyectoOptions}
              value={proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id))}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  PROYECTOS: value.map(p => p.id),
                  PROYECTO: value.map(p => p.nombre).join(','),
                  DIVISIONES: [],
                  DIVISION: '',
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: ''
                };
                setTempFilterData(updated);
              }}
              limitTags={1}
              renderTags={() => null}
              getOptionLabel={(option) => option?.nombre || ''}
              renderInput={(params) => (
                renderSummaryInput(
                  params,
                  'Proyecto',
                  proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id)).map(p => p.nombre)
                )
              )}
              sx={{ width: 'clamp(95px, 8vw, 118px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 4. Select Grupo / División (Nivel 3) */}
            <Autocomplete
              disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={divisionOptions}
              value={divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id))}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  DIVISIONES: value.map(d => d.id),
                  DIVISION: value.map(d => d.nombre).join(','),
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: ''
                };
                setTempFilterData(updated);
              }}
              limitTags={1}
              renderTags={() => null}
              getOptionLabel={(option) => option?.nombre || ''}
              renderInput={(params) => (
                renderSummaryInput(
                  params,
                  'Grupo',
                  divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id)).map(d => d.nombre)
                )
              )}
              sx={{ width: 'clamp(95px, 8vw, 115px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 5. Select Centro de Costo (Nivel 4) */}
            <Autocomplete
              disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
              multiple
              freeSolo
              forcePopupIcon
              size="small"
              options={centroCostoOptions}
              value={tempFilterData.CENTRO_COSTOS || []}
              onChange={(_, value) => updateVisibleFilter('CENTRO_COSTOS', value || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderInput={(params) => renderSummaryInput(params, 'Centro costo', tempFilterData.CENTRO_COSTOS || [])}
              sx={{ width: 'clamp(100px, 9vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 6. Select Departamento */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              forcePopupIcon
              size="small"
              options={departamentoOptions}
              value={tempFilterData.DEPARTAMENTOS || []}
              onChange={(_, value) => updateVisibleFilter('DEPARTAMENTOS', value || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderInput={(params) => renderSummaryInput(params, 'Departamento', tempFilterData.DEPARTAMENTOS || [])}
              sx={{ width: 'clamp(100px, 9vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 7. Select Proveedor */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              forcePopupIcon
              size="small"
              options={proveedorOptions}
              value={tempFilterData.PROVEEDORES || []}
              onChange={(_, value) => updateVisibleFilter('PROVEEDORES', value || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderInput={(params) => renderSummaryInput(params, 'Proveedor', tempFilterData.PROVEEDORES || [])}
              sx={{ width: 'clamp(105px, 9.5vw, 130px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 8. Select Divisa */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              forcePopupIcon
              size="small"
              options={divisaOptions}
              value={tempFilterData.DIVISAS || []}
              onChange={(_, value) => updateVisibleFilter('DIVISAS', value || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderInput={(params) => renderSummaryInput(params, 'Divisa', tempFilterData.DIVISAS || [])}
              sx={{ width: 'clamp(75px, 6vw, 90px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* Separador vertical */}
            <Box sx={{ borderLeft: '1px solid #cbd5e1', height: 20, mx: 0.3, flexShrink: 0 }} />

            {/* 9. Buscador General al final */}
            <TextField
              disabled={loadingData}
              size="small"
              placeholder="Buscar..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyFilters(tempFilterData, e.currentTarget.value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#0655A5', fontSize: 'clamp(12px, 0.9vw, 14px)' }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={loadingData}
                      size="small"
                      onClick={() => {
                        setQuickSearch('');
                        applyFilters(tempFilterData, '');
                      }}
                      sx={{ p: 0.1 }}
                    >
                      <ClearIcon sx={{ fontSize: 'clamp(10px, 0.8vw, 12px)' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: 'clamp(110px, 9vw, 135px)',
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  height: 'clamp(26px, 2vw, 28px)',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                  fontWeight: 500,
                  px: 0.8,
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#0655A5', borderWidth: '1.5px' },
                }
              }}
            />

            {/* 10. Botón Buscar */}
            <Button
              size="small"
              variant="contained"
              disabled={loadingData}
              onClick={() => applyFilters(tempFilterData, quickSearch)}
              startIcon={<SearchIcon sx={{ fontSize: 'clamp(11px, 0.8vw, 13px) !important' }} />}
              sx={{
                height: 'clamp(26px, 2vw, 28px)',
                px: 'clamp(8px, 0.8vw, 12px)',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                fontWeight: 700,
                borderRadius: '10px',
                bgcolor: '#0655A5',
                color: '#ffffff',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: '#054785' },
              }}
            >
              Buscar
            </Button>

            {/* 11. Botón Limpiar */}
            <Button
              size="small"
              variant="outlined"
              disabled={loadingData}
              onClick={clearFilters}
              startIcon={<RestartAltIcon sx={{ fontSize: 'clamp(11px, 0.8vw, 13px) !important' }} />}
              sx={{
                height: 'clamp(26px, 2vw, 28px)',
                px: 'clamp(6px, 0.6vw, 10px)',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                fontWeight: 600,
                borderRadius: '10px',
                borderColor: '#cbd5e1',
                color: '#64748b',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
              }}
            >
              Limpiar
            </Button>
          </Box>

        </Box>
      </Box>

      {/* ── POPOVER DE FILTROS AVANZADOS (< XL) ── */}
      <Popover
        open={showFiltersPanel}
        anchorEl={filterAnchorEl}
        onClose={() => setShowFiltersPanel(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 1.5,
            width: 'clamp(280px, 85vw, 360px)',
            borderRadius: '14px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            mt: 0.8
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          {/* Encabezado del Popover */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0.5, borderBottom: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>
              Filtros Avanzados
            </Typography>
            <IconButton size="small" onClick={() => setShowFiltersPanel(false)} sx={{ p: 0.2 }}>
              <ClearIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>

          {/* Rango de Fechas */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748b' }}>Rango de Fechas</Typography>
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_INICIO || ''}
                onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: false })}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    height: 28,
                    fontSize: '0.70rem',
                    bgcolor: '#ffffff',
                    borderRadius: '6px',
                    px: 0.6,
                    '& fieldset': { borderColor: '#cbd5e1' }
                  }
                }}
              />
              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>a</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_FIN || ''}
                onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: false })}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    height: 28,
                    fontSize: '0.70rem',
                    bgcolor: '#ffffff',
                    borderRadius: '6px',
                    px: 0.6,
                    '& fieldset': { borderColor: '#cbd5e1' }
                  }
                }}
              />
            </Box>
          </Box>

          {/* Empresa */}
          <Autocomplete
            disabled={loadingData}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={empresaOptions}
            value={empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                EMPRESA_IDS: value.map(empresa => empresa.id),
                EMPRESA: value.map(empresa => empresa.nombre).join(','),
                PROYECTOS: [],
                PROYECTO: '',
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
                DEPARTAMENTOS: [],
                PROVEEDORES: [],
                DIVISAS: [],
              };
              setTempFilterData(updated);
            }}
            limitTags={1}
            renderTags={() => null}
            getOptionLabel={(option) => option?.nombre || ''}
            renderInput={(params) => (
              renderSummaryInput(
                params,
                'Empresa',
                empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id)).map(e => e.nombre)
              )
            )}
            sx={{
              '& .MuiOutlinedInput-root': { height: '28px !important', borderRadius: '6px !important', fontSize: '0.70rem !important' }
            }}
          />

          {/* Proyecto */}
          <Autocomplete
            disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={proyectoOptions}
            value={proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                PROYECTOS: value.map(p => p.id),
                PROYECTO: value.map(p => p.nombre).join(','),
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: ''
              };
              setTempFilterData(updated);
            }}
            limitTags={1}
            renderTags={() => null}
            getOptionLabel={(option) => option?.nombre || ''}
            renderInput={(params) => (
              renderSummaryInput(
                params,
                'Proyecto',
                proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id)).map(p => p.nombre)
              )
            )}
            sx={{
              '& .MuiOutlinedInput-root': { height: '28px !important', borderRadius: '6px !important', fontSize: '0.70rem !important' }
            }}
          />

          {/* Grupo / División */}
          <Autocomplete
            disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={divisionOptions}
            value={divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                DIVISIONES: value.map(d => d.id),
                DIVISION: value.map(d => d.nombre).join(','),
                CENTRO_COSTOS: [],
                CENTRO_COSTO: ''
              };
              setTempFilterData(updated);
            }}
            limitTags={1}
            renderTags={() => null}
            getOptionLabel={(option) => option?.nombre || ''}
            renderInput={(params) => (
              renderSummaryInput(
                params,
                'Grupo',
                divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id)).map(d => d.nombre)
              )
            )}
            sx={{
              '& .MuiOutlinedInput-root': { height: '28px !important', borderRadius: '6px !important', fontSize: '0.70rem !important' }
            }}
          />

          {/* Centro de Costo */}
          <Autocomplete
            disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
            multiple
            freeSolo
            forcePopupIcon
            size="small"
            options={centroCostoOptions}
            value={tempFilterData.CENTRO_COSTOS || []}
            onChange={(_, value) => updateVisibleFilter('CENTRO_COSTOS', value || [], { apply: false })}
            limitTags={1}
            renderTags={() => null}
            renderInput={(params) => (
              renderSummaryInput(params, 'Centro costo', tempFilterData.CENTRO_COSTOS || [])
            )}
            sx={{
              '& .MuiOutlinedInput-root': { height: '28px !important', borderRadius: '6px !important', fontSize: '0.70rem !important' }
            }}
          />

          {/* Departamento */}
          <Autocomplete
            disabled={loadingData}
            multiple
            freeSolo
            forcePopupIcon
            size="small"
            options={departamentoOptions}
            value={tempFilterData.DEPARTAMENTOS || []}
            onChange={(_, value) => updateVisibleFilter('DEPARTAMENTOS', value || [], { apply: false })}
            limitTags={1}
            renderTags={() => null}
            renderInput={(params) => (
              renderSummaryInput(params, 'Departamento', tempFilterData.DEPARTAMENTOS || [])
            )}
            sx={{
              '& .MuiOutlinedInput-root': { height: '28px !important', borderRadius: '6px !important', fontSize: '0.70rem !important' }
            }}
          />

          {/* Proveedor */}
          <Autocomplete
            disabled={loadingData}
            multiple
            freeSolo
            forcePopupIcon
            size="small"
            options={proveedorOptions}
            value={tempFilterData.PROVEEDORES || []}
            onChange={(_, value) => updateVisibleFilter('PROVEEDORES', value || [], { apply: false })}
            limitTags={1}
            renderTags={() => null}
            renderInput={(params) => (
              renderSummaryInput(params, 'Proveedor', tempFilterData.PROVEEDORES || [])
            )}
            sx={{
              '& .MuiOutlinedInput-root': { height: '28px !important', borderRadius: '6px !important', fontSize: '0.70rem !important' }
            }}
          />

          {/* Divisa */}
          <Autocomplete
            disabled={loadingData}
            multiple
            freeSolo
            forcePopupIcon
            size="small"
            options={divisaOptions}
            value={tempFilterData.DIVISAS || []}
            onChange={(_, value) => updateVisibleFilter('DIVISAS', value || [], { apply: false })}
            limitTags={1}
            renderTags={() => null}
            renderInput={(params) => (
              renderSummaryInput(params, 'Divisa', tempFilterData.DIVISAS || [])
            )}
            sx={{
              '& .MuiOutlinedInput-root': { height: '28px !important', borderRadius: '6px !important', fontSize: '0.70rem !important' }
            }}
          />
        </Box>

        {/* Acciones de Footer del Popover */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.8, pt: 1, mt: 1, borderTop: '1px solid #f1f5f9' }}>
          <Button
            disabled={loadingData}
            size="small"
            variant="outlined"
            onClick={() => {
              setTempFilterData(baseFilters);
              setQuickSearch('');
              aplicarFiltros(baseFilters, '');
              setShowFiltersPanel(false);
            }}
            startIcon={<RestartAltIcon sx={{ fontSize: '12px !important' }} />}
            sx={{
              height: 26,
              px: 1.2,
              fontSize: '0.68rem',
              fontWeight: 600,
              borderRadius: '6px',
              borderColor: '#cbd5e1',
              color: '#64748b',
              textTransform: 'none',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
            }}
          >
            Restablecer
          </Button>

          <Button
            disabled={loadingData}
            size="small"
            variant="contained"
            onClick={() => {
              applyFilters(tempFilterData, quickSearch);
              setShowFiltersPanel(false);
            }}
            startIcon={<SearchIcon sx={{ fontSize: '12px !important' }} />}
            sx={{
              height: 26,
              px: 1.8,
              fontSize: '0.68rem',
              fontWeight: 700,
              borderRadius: '6px',
              bgcolor: '#0655A5',
              color: '#ffffff',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#054785' },
            }}
          >
            Aplicar
          </Button>
        </Box>
      </Popover>

      {/* Menú Flotante Masivo (Solo aparece cuando hay selección) */}
      {/* Barra de acciones masivas integrada en CollapsibleTable para evitar el salto de diseño */}

      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <CollapsibleTable
          loading={loadingData}
          dataTable={data?.contenido || []}
          buttons={buttons}
          showExportButton
          mobileCard={{ statusMode: 'none' }}
          serverPagination={true}
          totalServerRows={data?.total || 0}
          onStateChange={handleTableStateChange}
          hiddenFields={hiddenFieldsMemo}
          initialColumnOrder={initialColumnOrderMemo}
          rightButtons={<ExportButton data={data} onFetchAll={handleExportFetchAll} fileName="PagosAut.xlsx" />}
          columnLabels={columnLabelsMemo}
          stickyLeftKeys={[]}
          stickyLeftWidths={{ __select__: 34, __expand__: 34 }}
          compactActions
          maxInlineActions={3}
          persistEnabled
          persistKey="PagosAut"
          persistVista="layout_compacto"
          usuarioId={usuarioId}
          empresaId={empresaIdPersist}
          apiUrl={apiUrl}

          // 🔥 Habilitamos funcionalidad Multi-Select
          selectable={true}
          selectedRows={selectedRows}
          onSelectionChange={(selectedArray) => {
            setSelectedRows(selectedArray);
          }}
          selectionActions={
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleBulkRechazar}
                sx={{ fontWeight: 'bold', borderRadius: 2, textTransform: 'none' }}
              >
                Rechazar Seleccionados
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleBulkAutorizar}
                sx={{ fontWeight: 'bold', borderRadius: 2, textTransform: 'none', bgcolor: '#0655A5' }}
              >
                Autorizar Seleccionados
              </Button>
            </Box>
          }
        />
      </Box>

      <SimpleSuccessSpinner open={loadingAction} message="Procesando..." />

      <CancelacionModal
        open={modalStates.openCancelacionModal}
        onClose={() => setModalStates((prev) => ({ ...prev, openCancelacionModal: false }))}
        onSubmit={cancelarSolicitud}
        onObservacionesChange={(e) => setModalStates((prev) => ({ ...prev, observaciones: e.target.value }))}
        observaciones={modalStates.observaciones}
      />

      <SnackbarAlert
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />

      <FileActionModal
        open={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        files={fileList}
        initialFileId={initialFileId}
      />

    </Box>
  );
};

export default PagosAut;
